import { useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { getActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import {
  DialogListActionRow,
  DialogListBody,
  DialogListContentStack,
  DialogListSectionHeader,
  DialogInlineCode,
  Text,
} from '@/ui';
import { Button, HelpOutline, IconButton, Tooltip } from '@/ui/primitives';
import { AddIcon } from '@/ui/primitives/icons';
import { defaultTagColor, TableRowTagEditorRow } from './TableRowTagEditorRow';
import {
  tableRowTagsAddButtonIconSx,
  tableRowTagsAddButtonSx,
  tableRowTagsInfoButtonSx,
  tableRowTagsTooltipSlotSx,
  tableRowTagsTooltipTextSx,
} from './TableRowTagsDialog.styles';
import type { TableActionsCellProps } from './TableCells.types';
import type { TagColor } from '@/shared/data/tags';

type TableRowTagsDialogProps = {
  editTagsEntityNoun: NonNullable<TableActionsCellProps['editTagsEntityNoun']>;
  loading: boolean;
  onClose: () => void;
  onConfirm?: (input: { tags: string[]; colors: TagColor[] }) => void;
  open: boolean;
  rowName: string;
  rowTagColors: TagColor[];
  rowTags: string[];
};

const normalizeTagsAndColors = (
  tags: string[],
  colors: TagColor[],
): { tags: string[]; colors: TagColor[] } => {
  const normalizedTags = tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
  const normalizedColors =
    normalizedTags.length === colors.length
      ? colors
      : normalizedTags.map((_tag, index) => colors[index] ?? defaultTagColor);
  return { tags: normalizedTags, colors: normalizedColors };
};

const getTagCascadeCopy = (
  editTagsEntityNoun: NonNullable<TableActionsCellProps['editTagsEntityNoun']>,
): string => {
  if (editTagsEntityNoun === 'suite') {
    return 'Tags will be applied to all associated suite runs, tests and test runs.';
  }
  if (editTagsEntityNoun === 'code directory') return 'Tags will be applied to this directory.';
  if (editTagsEntityNoun === 'code file') return 'Tags will be applied to this file.';
  if (editTagsEntityNoun === 'coverage run') return 'Tags will be applied to this coverage run.';
  if (editTagsEntityNoun === 'feature') return 'Tags will be applied only to this feature.';
  if (editTagsEntityNoun === 'release') return 'Tags will be applied only to this release.';
  if (editTagsEntityNoun === 'upload') {
    return 'Tags will be applied to all associated results files, execution runs, suite runs and test runs.';
  }
  if (editTagsEntityNoun === 'results file') {
    return 'Tags will be applied to all associated execution runs, suite runs and test runs.';
  }
  if (editTagsEntityNoun === 'execution run') {
    return 'Tags will be applied to all associated suite runs and test runs.';
  }
  if (editTagsEntityNoun === 'suite run') {
    return 'Tags will be applied to all associated test runs.';
  }
  if (editTagsEntityNoun === 'test run') return 'Tags will be applied only to this test run.';
  return 'Tags will be applied to all associated test runs.';
};

export const TableRowTagsDialog = ({
  editTagsEntityNoun,
  loading,
  onClose,
  onConfirm,
  open,
  rowName,
  rowTagColors,
  rowTags,
}: TableRowTagsDialogProps): JSX.Element => {
  const [draftTags, setDraftTags] = useState<string[]>(rowTags.length > 0 ? rowTags : []);
  const [draftTagColors, setDraftTagColors] = useState<TagColor[]>(
    rowTags.length > 0 ? rowTags.map((_, index) => rowTagColors[index] ?? defaultTagColor) : [],
  );

  useEffect(() => {
    if (!open) return;
    setDraftTags(rowTags.length > 0 ? rowTags : []);
    setDraftTagColors(
      rowTags.length > 0 ? rowTags.map((_, index) => rowTagColors[index] ?? defaultTagColor) : [],
    );
  }, [open, rowTagColors, rowTags]);

  const handleTagChange = (index: number, value: string): void => {
    setDraftTags((prev) => prev.map((item, itemIndex) => (itemIndex === index ? value : item)));
  };
  const handleTagColorChange = (index: number, color: TagColor): void => {
    setDraftTagColors((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? color : item)),
    );
  };
  const handleRemoveTag = (index: number): void => {
    setDraftTags((prev) => prev.filter((_item, itemIndex) => itemIndex !== index));
    setDraftTagColors((prev) => prev.filter((_item, itemIndex) => itemIndex !== index));
  };

  const normalizedInitialTagsAndColors = normalizeTagsAndColors(
    rowTags.length > 0 ? rowTags : [],
    rowTags.length > 0 ? rowTags.map((_, index) => rowTagColors[index] ?? defaultTagColor) : [],
  );
  const normalizedDraftTagsAndColors = normalizeTagsAndColors(draftTags, draftTagColors);
  const hasTagChanges =
    normalizedDraftTagsAndColors.tags.length !== normalizedInitialTagsAndColors.tags.length ||
    normalizedDraftTagsAndColors.colors.length !== normalizedInitialTagsAndColors.colors.length ||
    normalizedDraftTagsAndColors.tags.some(
      (tag, index) => tag !== normalizedInitialTagsAndColors.tags[index],
    ) ||
    normalizedDraftTagsAndColors.colors.some(
      (color, index) => color !== normalizedInitialTagsAndColors.colors[index],
    );
  const cascadeCopy = getTagCascadeCopy(editTagsEntityNoun);

  return (
    <ConfirmDialog
      open={open}
      title={getDialogMessage('entity.edit.title', { entity: 'tag', plural: true })}
      description={
        <Text role="body" color="textSecondary">
          Update tags for {editTagsEntityNoun}{' '}
          <DialogInlineCode component="span" monospace={false}>
            {rowName}
          </DialogInlineCode>
          .
        </Text>
      }
      confirmLabel={getActionLabel('update')}
      confirmColor="primary"
      confirmDisabled={!hasTagChanges}
      loading={loading}
      onClose={onClose}
      onConfirm={() => {
        onConfirm?.(normalizedDraftTagsAndColors);
        onClose();
      }}
    >
      <DialogListBody>
        <DialogListSectionHeader
          endSlot={
            <Tooltip
              title={
                <Text role="body" sx={tableRowTagsTooltipTextSx}>
                  {cascadeCopy}
                </Text>
              }
              placement="top"
              slotProps={{
                tooltip: {
                  sx: tableRowTagsTooltipSlotSx,
                },
              }}
            >
              <IconButton
                size="small"
                aria-label="More information about tags"
                sx={tableRowTagsInfoButtonSx}
              >
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        >
          <Text role="label" color="textSecondary">
            Tags
          </Text>
        </DialogListSectionHeader>
        <DialogListContentStack>
          {draftTags.map((tag, index) => (
            <TableRowTagEditorRow
              key={`${rowName}-tag-${index}`}
              index={index}
              onColorChange={handleTagColorChange}
              onRemove={handleRemoveTag}
              onTagChange={handleTagChange}
              selectedColor={draftTagColors[index] ?? defaultTagColor}
              tag={tag}
            />
          ))}
        </DialogListContentStack>
        <DialogListActionRow>
          <Button
            variant="text"
            startIcon={<AddIcon sx={tableRowTagsAddButtonIconSx} />}
            onClick={() => {
              setDraftTags((prev) => [...prev, '']);
              setDraftTagColors((prev) => [...prev, defaultTagColor]);
            }}
            sx={tableRowTagsAddButtonSx}
          >
            {getActionLabel('addTag')}
          </Button>
        </DialogListActionRow>
      </DialogListBody>
    </ConfirmDialog>
  );
};
